import type { StageTagFailure, WriteRejection } from "@estelle/domain";
import { STAGE_TAGS } from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useId, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";

/**
 * The `ERP: <stage>` tag in Shopify (#465 part 2), on Settings > Order stages.
 *
 * Off by default. While it is on, the ERP keeps one stage tag on each open
 * order in Shopify. A write Shopify refuses is listed here with a Retry; the
 * stage shown in the ERP is right either way.
 */

const TAG_NAMES = Array.from(
  new Set(Object.values(STAGE_TAGS).filter((tag) => tag !== null)),
);

function Failures({ items }: { items: StageTagFailure[] }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const retry = useMutation({
    mutationFn: () => data.stages.retryTags(),
    onSuccess: (result) => {
      if (result.ok) {
        setMessage(
          result.data.failed === 0
            ? "Tried again: every order is tagged."
            : `Tried again: ${String(result.data.failed)} still refused.`,
        );
      }
      void queryClient.invalidateQueries({
        queryKey: ["order-stages", "tag-failures"],
      });
    },
  });
  return (
    <div className="border-destructive/40 flex flex-col gap-2 rounded-md border p-3">
      <p className="text-sm font-medium">
        {items.length === 1
          ? "1 order could not be tagged in Shopify"
          : `${String(items.length)} orders could not be tagged in Shopify`}
      </p>
      <ul className="flex flex-col gap-1 text-sm">
        {items.map((item) => (
          <li key={item.orderId}>
            <span className="font-medium">{item.orderNumber}</span>{" "}
            <span className="text-muted-foreground">{item.error}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={retry.isPending}
          onClick={() => {
            setMessage(null);
            retry.mutate();
          }}
        >
          {retry.isPending ? <Loader2 className="animate-spin" /> : null}
          Retry
        </Button>
        {message ? (
          <span role="status" className="text-muted-foreground text-sm">
            {message}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function StageTagSwitch() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const checkId = useId();
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const settings = useQuery({
    queryKey: ["order-stages", "tag-settings"],
    queryFn: () => data.stages.getTagSettings(),
  });
  const failures = useQuery({
    queryKey: ["order-stages", "tag-failures"],
    queryFn: () => data.stages.tagFailures(),
    enabled: settings.data?.enabled === true,
    refetchInterval: 60_000,
  });
  const save = useMutation({
    mutationFn: (enabled: boolean) => data.stages.saveTagSettings(enabled),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        queryClient.setQueryData(["order-stages", "tag-settings"], result.data);
        void queryClient.invalidateQueries({
          queryKey: ["order-stages", "tag-failures"],
        });
      } else setRejection(result.rejection);
    },
  });

  // A read refused for want of permission leaves the section out; changing the
  // switch needs the tags permission, which the server checks.
  if (settings.isPending || settings.isError) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <Checkbox
          id={checkId}
          checked={settings.data.enabled}
          disabled={save.isPending}
          onCheckedChange={(value) => {
            save.mutate(value === true);
          }}
        />
        <div className="flex flex-col gap-1">
          <Label htmlFor={checkId} className="text-sm font-medium">
            Write the stage to Shopify as a tag
          </Label>
          <p className="text-muted-foreground text-xs">
            One tag per open order, kept current: {TAG_NAMES.join(", ")}. Orders
            already finished when you turn this on are not tagged. Before
            turning it on, check that Shopify Flow, Interakt and Buddy ignore
            tags that start with ERP:.
          </p>
        </div>
      </div>
      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            setRejection(null);
          }}
        />
      ) : null}
      {settings.data.enabled && failures.data && failures.data.length > 0 ? (
        <Failures items={failures.data} />
      ) : null}
    </div>
  );
}
